/**
 * Artist Portal — per-talent dashboard.
 *
 * Surface:
 *   - Talent picker (when the logged-in user has multiple linked talents).
 *   - Upcoming shows roll-up (from the existing /api/talent/:id/runsheet
 *     endpoint when present, or a derived projection from /api/clients).
 *   - Crew & venue address book (per-talent).
 *   - Call sheets attached to the talent's bookings (link to editor).
 *   - Slot for the Talent Billing tab (M4A — added via lazy import).
 *
 * Auth: artist role (talent.userId === currentUser.userId) sees their own
 * talent. Admins see a talent picker covering every talent in the workspace.
 */
import { useEffect, useMemo, useState } from "react";
import { Link, useLocation } from "wouter";
import { useQuery, useMutation } from "@tanstack/react-query";
import { useIsMobile } from "@/hooks/use-mobile";
import BackstageHome from "@/components/backstage/backstage-home";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { CollaboratorRolesManager } from "@/components/manage-collaborator-roles";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { VoiceToTextTextarea as Textarea } from "@/components/voice-field";
import { Badge } from "@/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Switch } from "@/components/ui/switch";
import {
  Loader2,
  Calendar,
  MapPin,
  Phone,
  Mail,
  Plus,
  Star,
  Trash2,
  Pencil,
  ClipboardList,
  Users,
  Wallet,
  Send,
  Lock,
  HelpCircle,
  UserCog,
  Contact as ContactIcon,
} from "lucide-react";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { format, parseISO } from "date-fns";
import {
  toEventDate,
  compareEventDate,
  formatEventDate,
  parseEventDate,
} from "@shared/event-date";
import type { Talent, UserProfile, TalentContact, ShowCallSheet, TalentMember } from "@shared/schema";
import TalentBillingTab from "@/components/talent-billing-tab";
import ArtistTourWidget from "@/components/artist-tour-widget";

type MyAccessEntry = {
  talent: Talent;
  accessType: "legacy_linked" | "member" | "admin";
  canWrite: boolean;
  isPrimary: boolean;
  memberId: string | null;
};

type ContactRole =
  | "sound_tech"
  | "driver"
  | "hair_makeup"
  | "venue_mgr"
  | "hotel"
  | "promoter_contact"
  | "tour_manager"
  | "opener"
  | "merch"
  | "photographer"
  | "caterer"
  | "security"
  | "other";

const ROLE_OPTIONS: Array<{ value: ContactRole; label: string }> = [
  { value: "sound_tech", label: "Sound Tech" },
  { value: "driver", label: "Driver" },
  { value: "hair_makeup", label: "Hair / Makeup" },
  { value: "venue_mgr", label: "Venue Manager" },
  { value: "hotel", label: "Hotel" },
  { value: "promoter_contact", label: "Promoter" },
  { value: "tour_manager", label: "Tour Manager" },
  { value: "opener", label: "Opener" },
  { value: "merch", label: "Merch" },
  { value: "photographer", label: "Photographer" },
  { value: "caterer", label: "Caterer" },
  { value: "security", label: "Security" },
  { value: "other", label: "Other" },
];

function safeFmtDate(value: string | Date | null | undefined): string {
  if (!value) return "TBD";
  try {
    // Task #112 — canonical YYYY-MM-DD strings are date-only; parseISO gives
    // local midnight so the calendar day never drifts across timezones.
    if (typeof value === "string" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
      return format(parseISO(value), "EEE, MMM d");
    }
    const d = typeof value === "string" ? parseISO(value) : value;
    return format(d, "EEE, MMM d • h:mm a");
  } catch {
    return "TBD";
  }
}

type EarningsResponse = {
  months: Array<{ month: string; paidDollars: number; pendingDollars: number }>;
  totals: { paidDollars: number; pendingDollars: number; projectedDollars: number };
};

function EarningsProjectionCard({ talentId }: { talentId: string | null }) {
  const { data, isLoading } = useQuery<EarningsResponse>({
    queryKey: ["/api/talent/earnings-projection", talentId],
    queryFn: async () => {
      const url = talentId
        ? `/api/talent/earnings-projection?talentId=${encodeURIComponent(talentId)}`
        : `/api/talent/earnings-projection`;
      const r = await fetch(url, { credentials: "include" });
      if (!r.ok) throw new Error("failed to load earnings projection");
      return r.json();
    },
    enabled: !!talentId,
  });

  const fmt = (n: number) =>
    new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n);

  const max = (data?.months ?? []).reduce(
    (m, mo) => Math.max(m, mo.paidDollars + mo.pendingDollars),
    0,
  );

  return (
    <Card data-testid="card-earnings-projection">
      <CardHeader className="pb-3">
        <CardTitle className="text-base">Earnings Projection</CardTitle>
        <CardDescription>
          Next 6 months — confirmed shows × your rate, split into paid vs pending.
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="flex items-center justify-center p-6">
            <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
          </div>
        ) : !data || data.months.length === 0 ? (
          <p className="text-sm text-muted-foreground italic">No projected earnings.</p>
        ) : (
          <>
            <div className="grid grid-cols-3 gap-3 mb-4">
              <div>
                <div className="text-xs text-muted-foreground">Paid</div>
                <div className="text-lg font-semibold tabular-nums text-emerald-600 dark:text-emerald-400" data-testid="text-earnings-paid">
                  {fmt(data.totals.paidDollars)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Pending</div>
                <div className="text-lg font-semibold tabular-nums" data-testid="text-earnings-pending">
                  {fmt(data.totals.pendingDollars)}
                </div>
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Projected total</div>
                <div className="text-lg font-semibold tabular-nums" data-testid="text-earnings-projected">
                  {fmt(data.totals.projectedDollars)}
                </div>
              </div>
            </div>
            <div className="space-y-2">
              {data.months.map((mo) => {
                const total = mo.paidDollars + mo.pendingDollars;
                const widthPct = max > 0 ? (total / max) * 100 : 0;
                const paidPct = total > 0 ? (mo.paidDollars / total) * 100 : 0;
                const [year, month] = mo.month.split("-");
                const label = new Date(parseInt(year, 10), parseInt(month, 10) - 1, 1).toLocaleString("en-US", {
                  month: "short",
                  year: "2-digit",
                });
                return (
                  <div key={mo.month} className="flex items-center gap-3" data-testid={`row-month-${mo.month}`}>
                    <div className="w-14 text-xs text-muted-foreground">{label}</div>
                    <div className="flex-1 h-5 bg-muted rounded overflow-hidden relative">
                      <div
                        className="absolute inset-y-0 left-0 bg-emerald-500/70"
                        style={{ width: `${widthPct * (paidPct / 100)}%` }}
                      />
                      <div
                        className="absolute inset-y-0 bg-blue-500/60"
                        style={{ left: `${widthPct * (paidPct / 100)}%`, width: `${widthPct * (1 - paidPct / 100)}%` }}
                      />
                    </div>
                    <div className="w-24 text-right text-sm tabular-nums">{fmt(total)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export default function ArtistPortalPage() {
  const isMobile = useIsMobile();
  if (isMobile) return <BackstageHome />;
  return <ArtistPortalDesktop />;
}

function ArtistPortalDesktop() {
  const { toast } = useToast();
  const [, navigate] = useLocation();

  const { data: profile } = useQuery<UserProfile>({ queryKey: ["/api/profile"] });
  const { data: accessList = [], isLoading: talentLoading } = useQuery<MyAccessEntry[]>({
    queryKey: ["/api/talent/my-access"],
  });

  const isAdmin = profile?.role === "admin" || profile?.role === "super_admin";
  const ownedTalents = useMemo(() => accessList.map((e) => e.talent), [accessList]);

  const [selectedTalentId, setSelectedTalentId] = useState<string | null>(null);
  useEffect(() => {
    if (!selectedTalentId && ownedTalents.length > 0) {
      setSelectedTalentId(ownedTalents[0].id);
    }
  }, [ownedTalents, selectedTalentId]);

  const selectedTalent = ownedTalents.find((t) => t.id === selectedTalentId);
  const selectedAccess = accessList.find((e) => e.talent.id === selectedTalentId);
  const canWrite = selectedAccess?.canWrite ?? false;
  const isPrimaryOrAdmin = isAdmin || (selectedAccess?.isPrimary ?? false);

  // ── Members vs. Collaborators one-time explainer dialogs ──────────
  // Two independent, per-user, one-time educational dialogs. The "seen"
  // state is persisted on the user profile so they stay dismissed across
  // devices/sessions. Only portal managers (primary contact or admin) ever
  // see them — they're the ones who add members/collaborators.
  const [activeTab, setActiveTab] = useState("shows");
  const [membersExplainerOpen, setMembersExplainerOpen] = useState(false);
  const [collabExplainerOpen, setCollabExplainerOpen] = useState(false);
  const membersExplainerSeen = !!profile?.portalMembersExplainerSeenAt;
  const collabExplainerSeen = !!profile?.portalCollaboratorsExplainerSeenAt;

  const ackExplainer = useMutation({
    mutationFn: async (explainer: "members" | "collaborators") => {
      await apiRequest("PATCH", "/api/profile/portal-explainer-seen", { explainer, seen: true });
      return explainer;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/profile"] });
    },
  });

  // Auto-open each explainer the first time its tab is opened (managers only).
  useEffect(() => {
    if (!profile || !isPrimaryOrAdmin) return;
    if (activeTab === "team" && !membersExplainerSeen) {
      setMembersExplainerOpen(true);
    }
    if (activeTab === "contacts" && !collabExplainerSeen) {
      setCollabExplainerOpen(true);
    }
  }, [activeTab, profile, isPrimaryOrAdmin, membersExplainerSeen, collabExplainerSeen]);

  // ── Address book ──────────────────────────────────────────────
  const { data: contacts = [], isLoading: contactsLoading } = useQuery<
    TalentContact[]
  >({
    queryKey: ["/api/talent-contacts", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(
        `/api/talent-contacts?talentId=${encodeURIComponent(selectedTalentId!)}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("failed to load contacts");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  // ── Call sheets ──────────────────────────────────────────────
  const { data: callSheets = [], isLoading: sheetsLoading } = useQuery<ShowCallSheet[]>({
    queryKey: ["/api/call-sheets", "byTalent", selectedTalentId],
    queryFn: async () => {
      const r = await fetch(
        `/api/call-sheets?talentId=${encodeURIComponent(selectedTalentId!)}`,
        { credentials: "include" },
      );
      if (!r.ok) throw new Error("failed to load call sheets");
      return r.json();
    },
    enabled: !!selectedTalentId,
  });

  // ── Upcoming shows projection (cheap projection from clients endpoint) ──
  const { data: upcoming = [] } = useQuery<any[]>({
    queryKey: ["/api/clients", "upcoming"],
    queryFn: async () => {
      const r = await fetch("/api/clients?status=active&limit=200", {
        credentials: "include",
      });
      if (!r.ok) return [];
      const json = await r.json();
      const list: any[] = Array.isArray(json) ? json : json?.items || [];
      const now = Date.now();
      // Task #112 — compare by canonical YYYY-MM-DD via shared helpers.
      // parseEventDate anchors the day at noon UTC so "upcoming" never
      // walks across a tz boundary; compareEventDate sorts lexicographically
      // on the canonical string.
      const cutoff = now - 24 * 60 * 60 * 1000;
      return list
        .filter((c) => {
          const d = parseEventDate(c.dateOfEvent);
          return d != null && d.getTime() >= cutoff;
        })
        .filter(
          (c) =>
            !selectedTalentId ||
            c.talentId === selectedTalentId ||
            (c.talentIds && c.talentIds.includes(selectedTalentId)),
        )
        .sort((a, b) => compareEventDate(a.dateOfEvent, b.dateOfEvent))
        .slice(0, 12);
    },
    enabled: !!selectedTalentId,
  });

  // ── Contact dialog state ──────────────────────────────────────
  const [contactDialog, setContactDialog] = useState<{
    open: boolean;
    editing: TalentContact | null;
  }>({ open: false, editing: null });
  const [contactForm, setContactForm] = useState({
    name: "",
    role: "other" as ContactRole,
    email: "",
    phone: "",
    company: "",
    address: "",
    notes: "",
    isFavorite: false,
  });

  function openContactDialog(c: TalentContact | null) {
    if (c) {
      setContactForm({
        name: c.name || "",
        role: (c.role as ContactRole) || "other",
        email: c.email || "",
        phone: c.phone || "",
        company: c.company || "",
        address: c.address || "",
        notes: c.notes || "",
        isFavorite: c.isFavorite,
      });
    } else {
      setContactForm({
        name: "",
        role: "other",
        email: "",
        phone: "",
        company: "",
        address: "",
        notes: "",
        isFavorite: false,
      });
    }
    setContactDialog({ open: true, editing: c });
  }

  const upsertContact = useMutation({
    mutationFn: async () => {
      if (!selectedTalentId) throw new Error("no talent selected");
      if (contactDialog.editing) {
        const r = await apiRequest(
          "PATCH",
          `/api/talent-contacts/${contactDialog.editing.id}`,
          contactForm,
        );
        return r.json();
      }
      const r = await apiRequest("POST", "/api/talent-contacts", {
        talentId: selectedTalentId,
        ...contactForm,
      });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent-contacts", selectedTalentId],
      });
      setContactDialog({ open: false, editing: null });
      toast({ title: "Saved", description: "Contact saved." });
    },
    onError: (err: any) => {
      toast({
        title: "Error",
        description: err?.message || "Failed to save contact",
        variant: "destructive",
      });
    },
  });

  const deleteContact = useMutation({
    mutationFn: async (contactId: string) => {
      await apiRequest("DELETE", `/api/talent-contacts/${contactId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["/api/talent-contacts", selectedTalentId],
      });
      toast({ title: "Removed", description: "Contact removed." });
    },
  });

  // ── Portal members (Team tab) — only loaded for primary contacts + admins ─
  const { data: portalMembers = [], isLoading: membersLoading, refetch: refetchMembers } = useQuery<TalentMember[]>({
    queryKey: ["/api/portal/talent", selectedTalentId, "members"],
    queryFn: async () => {
      const r = await fetch(`/api/portal/talent/${selectedTalentId}/members`, { credentials: "include" });
      if (!r.ok) throw new Error("failed to load members");
      return r.json();
    },
    enabled: !!selectedTalentId && isPrimaryOrAdmin,
  });

  const sendInvite = useMutation({
    mutationFn: async (memberId: string) => {
      const r = await apiRequest("POST", `/api/portal/talent/${selectedTalentId}/members/${memberId}/invite`);
      return r.json();
    },
    onSuccess: () => {
      toast({ title: "Invite sent", description: "An email invitation was sent to the member." });
      refetchMembers();
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Failed to send invite", variant: "destructive" }),
  });

  const updateMemberAccess = useMutation({
    mutationFn: async ({ memberId, accessLevel }: { memberId: string; accessLevel: "read" | "write" }) => {
      const r = await apiRequest("PATCH", `/api/portal/talent/${selectedTalentId}/members/${memberId}`, { accessLevel });
      return r.json();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/portal/talent", selectedTalentId, "members"] });
      toast({ title: "Updated", description: "Access level updated." });
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Failed to update", variant: "destructive" }),
  });

  const removeMember = useMutation({
    mutationFn: async (memberId: string) => {
      await apiRequest("DELETE", `/api/portal/talent/${selectedTalentId}/members/${memberId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/portal/talent", selectedTalentId, "members"] });
      toast({ title: "Removed", description: "Member removed from portal access." });
    },
    onError: (err: any) => toast({ title: "Error", description: err?.message || "Failed to remove", variant: "destructive" }),
  });

  // ── Create call sheet from a booking ──────────────────────────
  const createCallSheet = useMutation({
    mutationFn: async (booking: any) => {
      const r = await apiRequest("POST", "/api/call-sheets", {
        bookingId: booking.id,
        // Task #112 — use canonical event-date helper so the title
        // never shifts a day across timezones.
        title: `${booking.accountName || "Show"} — ${formatEventDate(booking.dateOfEvent, { variant: "short" }) || "TBD"}`,
        eventDate: booking.dateOfEvent || null,
        venueName: booking.accountName || null,
        venueAddress: booking.address || null,
      });
      return r.json();
    },
    onSuccess: (sheet: any) => {
      queryClient.invalidateQueries({
        queryKey: ["/api/call-sheets", "byTalent", selectedTalentId],
      });
      toast({ title: "Created", description: "Call sheet created." });
      navigate(`/call-sheet/${sheet.id}`);
    },
    onError: (err: any) =>
      toast({
        title: "Error",
        description: err?.message || "Failed to create call sheet",
        variant: "destructive",
      }),
  });

  if (talentLoading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="loading-portal">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (ownedTalents.length === 0) {
    return (
      <div className="container mx-auto p-6">
        <Card>
          <CardHeader>
            <CardTitle>Talent Profile</CardTitle>
            <CardDescription>
              You don&apos;t have a talent profile linked to your account yet.
              Ask an admin to link you to your talent record from the Profiles page.
            </CardDescription>
          </CardHeader>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-4 sm:p-6 space-y-6 max-w-6xl">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold tracking-tight" data-testid="text-artist-portal-title">
            Talent Profile
          </h1>
          <p className="text-muted-foreground text-sm">
            Your shows, your address book, and your call sheets.
          </p>
        </div>
        {ownedTalents.length > 1 && (
          <Select
            value={selectedTalentId ?? ""}
            onValueChange={(v) => setSelectedTalentId(v)}
          >
            <SelectTrigger className="w-[240px]" data-testid="select-talent">
              <SelectValue placeholder="Select talent" />
            </SelectTrigger>
            <SelectContent>
              {ownedTalents.map((t) => (
                <SelectItem key={t.id} value={t.id} data-testid={`option-talent-${t.id}`}>
                  {t.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className={`grid w-full ${isPrimaryOrAdmin ? "grid-cols-6" : "grid-cols-5"}`} data-testid="tabs-portal">
          <TabsTrigger value="shows" data-testid="tab-shows">
            <Calendar className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Shows</span>
          </TabsTrigger>
          <TabsTrigger value="tours" data-testid="tab-tours">
            <MapPin className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Tours</span>
          </TabsTrigger>
          <TabsTrigger value="contacts" data-testid="tab-contacts">
            <Users className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Contacts</span>
          </TabsTrigger>
          <TabsTrigger value="callsheets" data-testid="tab-callsheets">
            <ClipboardList className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Call Sheets</span>
          </TabsTrigger>
          <TabsTrigger value="billing" data-testid="tab-billing">
            <Wallet className="h-4 w-4 mr-2" />
            <span className="hidden sm:inline">Billing</span>
          </TabsTrigger>
          {isPrimaryOrAdmin && (
            <TabsTrigger value="team" data-testid="tab-team">
              <Users className="h-4 w-4 mr-2" />
              <span className="hidden sm:inline">Team</span>
            </TabsTrigger>
          )}
        </TabsList>

        {/* ── Tours tab — read-only widget for the selected talent ─── */}
        <TabsContent value="tours" className="space-y-4 mt-4">
          {selectedTalent && <ArtistTourWidget talentId={selectedTalent.id} />}
        </TabsContent>

        {/* ── Shows tab ─────────────────────────────────────────── */}
        <TabsContent value="shows" className="space-y-4 mt-4">
          <EarningsProjectionCard talentId={selectedTalentId} />
          <Card>
            <CardHeader>
              <CardTitle>Upcoming Shows</CardTitle>
              <CardDescription>
                Bookings on the calendar for {selectedTalent?.name || "you"}.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {upcoming.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="text-no-upcoming">
                  No upcoming shows.
                </p>
              ) : (
                <div className="space-y-3">
                  {upcoming.map((s) => (
                    <div
                      key={s.id}
                      className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 border rounded-lg p-3 hover-elevate"
                      data-testid={`row-show-${s.id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="font-medium truncate">
                          {s.accountName || "Untitled Show"}
                        </p>
                        <p className="text-xs text-muted-foreground">
                          {safeFmtDate(s.dateOfEvent)}
                          {s.address ? ` • ${s.address.split(",")[0]}` : ""}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        {callSheets.find((cs) => cs.bookingId === s.id) ? (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => {
                              const cs = callSheets.find((c) => c.bookingId === s.id)!;
                              navigate(`/call-sheet/${cs.id}`);
                            }}
                            data-testid={`button-open-callsheet-${s.id}`}
                          >
                            Open Call Sheet
                          </Button>
                        ) : canWrite ? (
                          <Button
                            size="sm"
                            onClick={() => createCallSheet.mutate(s)}
                            disabled={createCallSheet.isPending}
                            data-testid={`button-create-callsheet-${s.id}`}
                          >
                            <Plus className="h-3.5 w-3.5 mr-1" />
                            Call Sheet
                          </Button>
                        ) : null}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Contacts tab ──────────────────────────────────────── */}
        <TabsContent value="contacts" className="space-y-4 mt-4">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-1.5">
                  Address Book
                  {isPrimaryOrAdmin && (
                    <Button
                      size="icon"
                      variant="ghost"
                      className="h-6 w-6 text-muted-foreground"
                      onClick={() => setCollabExplainerOpen(true)}
                      aria-label="What's a collaborator?"
                      data-testid="button-collaborators-explainer"
                    >
                      <HelpCircle className="h-4 w-4" />
                    </Button>
                  )}
                </CardTitle>
                <CardDescription>
                  Sound techs, drivers, venue contacts you work with.
                </CardDescription>
              </div>
              {canWrite && (
                <Button
                  size="sm"
                  onClick={() => openContactDialog(null)}
                  data-testid="button-add-contact"
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Contact
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {contactsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : contacts.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="text-no-contacts">
                  No contacts yet. Add the people you book with regularly.
                </p>
              ) : (
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  {contacts.map((c) => (
                    <div
                      key={c.id}
                      className="border rounded-lg p-3 space-y-2 hover-elevate"
                      data-testid={`card-contact-${c.id}`}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate flex items-center gap-1">
                            {c.isFavorite && (
                              <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                            )}
                            {c.name}
                          </p>
                          <Badge variant="secondary" className="text-xs mt-1">
                            {ROLE_OPTIONS.find((r) => r.value === c.role)?.label || c.role}
                          </Badge>
                        </div>
                        {canWrite && (
                          <div className="flex gap-1">
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7"
                              onClick={() => openContactDialog(c)}
                              data-testid={`button-edit-contact-${c.id}`}
                            >
                              <Pencil className="h-3.5 w-3.5" />
                            </Button>
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-7 w-7 text-destructive"
                              onClick={() => deleteContact.mutate(c.id)}
                              data-testid={`button-delete-contact-${c.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          </div>
                        )}
                      </div>
                      {c.company && (
                        <p className="text-xs text-muted-foreground">{c.company}</p>
                      )}
                      <div className="flex flex-wrap gap-3 text-xs">
                        {c.email && (
                          <a
                            href={`mailto:${c.email}`}
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <Mail className="h-3 w-3" />
                            {c.email}
                          </a>
                        )}
                        {c.phone && (
                          <a
                            href={`tel:${c.phone}`}
                            className="flex items-center gap-1 text-primary hover:underline"
                          >
                            <Phone className="h-3 w-3" />
                            {c.phone}
                          </a>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Call sheets tab ───────────────────────────────────── */}
        <TabsContent value="callsheets" className="space-y-4 mt-4">
          <Card>
            <CardHeader>
              <CardTitle>Call Sheets</CardTitle>
              <CardDescription>
                Sectioned schedules tied to your bookings.
              </CardDescription>
            </CardHeader>
            <CardContent>
              {sheetsLoading ? (
                <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
              ) : callSheets.length === 0 ? (
                <p className="text-sm text-muted-foreground" data-testid="text-no-sheets">
                  No call sheets yet. Create one from any upcoming show on the
                  Shows tab.
                </p>
              ) : (
                <div className="space-y-2">
                  {callSheets.map((cs) => (
                    <Link
                      key={cs.id}
                      href={`/call-sheet/${cs.id}`}
                      data-testid={`link-callsheet-${cs.id}`}
                    >
                      <div className="border rounded-lg p-3 hover-elevate cursor-pointer flex items-center justify-between">
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{cs.title}</p>
                          <p className="text-xs text-muted-foreground">
                            {safeFmtDate(cs.eventDate as any)}
                            {cs.venueName ? ` • ${cs.venueName}` : ""}
                          </p>
                        </div>
                        <Badge
                          variant={cs.status === "published" ? "default" : "outline"}
                          className="text-xs"
                        >
                          {cs.status}
                        </Badge>
                      </div>
                    </Link>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ── Billing tab (M4A) ─────────────────────────────────── */}
        <TabsContent value="billing" className="mt-4">
          {selectedTalent ? (
            <TalentBillingTab talent={selectedTalent} isAdmin={!!isAdmin} />
          ) : null}
        </TabsContent>

        {/* ── Team tab — portal member management ───────────────── */}
        {isPrimaryOrAdmin && (
          <TabsContent value="team" className="space-y-4 mt-4">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-1.5">
                  Portal Team Access
                  <Button
                    size="icon"
                    variant="ghost"
                    className="h-6 w-6 text-muted-foreground"
                    onClick={() => setMembersExplainerOpen(true)}
                    aria-label="What's a member?"
                    data-testid="button-members-explainer"
                  >
                    <HelpCircle className="h-4 w-4" />
                  </Button>
                </CardTitle>
                <CardDescription>
                  Band and troupe members who have portal login access for{" "}
                  {selectedTalent?.name || "this talent"}. Each member logs in with their own
                  Replit account. The primary contact has write access; other members default
                  to read-only.
                </CardDescription>
              </CardHeader>
              <CardContent>
                {membersLoading ? (
                  <div className="flex items-center justify-center p-6">
                    <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
                  </div>
                ) : portalMembers.length === 0 ? (
                  <p className="text-sm text-muted-foreground italic" data-testid="text-no-members">
                    No portal members yet. Add members from the Talent Profile admin panel, then
                    use the Invite button here to send them a login link.
                  </p>
                ) : (
                  <div className="space-y-3">
                    {portalMembers.map((m) => (
                      <div
                        key={m.id}
                        className="border rounded-lg p-3 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3"
                        data-testid={`row-member-${m.id}`}
                      >
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <p className="font-medium truncate">{m.name}</p>
                            {m.isPrimaryContact && (
                              <Badge variant="default" className="text-xs">Primary</Badge>
                            )}
                            <Badge
                              variant={m.accessLevel === "write" ? "secondary" : "outline"}
                              className="text-xs"
                            >
                              {m.accessLevel === "write" ? "Read & Write" : "Read Only"}
                            </Badge>
                            {m.userId ? (
                              <Badge variant="outline" className="text-xs text-green-600 border-green-300">
                                Linked
                              </Badge>
                            ) : m.portalInviteStatus === "pending" ? (
                              <Badge variant="outline" className="text-xs text-yellow-600 border-yellow-300">
                                Invite sent
                              </Badge>
                            ) : null}
                          </div>
                          {m.email && (
                            <p className="text-xs text-muted-foreground mt-0.5">{m.email}</p>
                          )}
                        </div>
                        <div className="flex items-center gap-2 flex-wrap">
                          {!m.isPrimaryContact && (
                            <Select
                              value={m.accessLevel ?? "read"}
                              onValueChange={(v) =>
                                updateMemberAccess.mutate({ memberId: m.id, accessLevel: v as "read" | "write" })
                              }
                            >
                              <SelectTrigger className="h-8 w-36 text-xs" data-testid={`select-access-${m.id}`}>
                                <SelectValue />
                              </SelectTrigger>
                              <SelectContent>
                                <SelectItem value="read">Read Only</SelectItem>
                                <SelectItem value="write">Read & Write</SelectItem>
                              </SelectContent>
                            </Select>
                          )}
                          {m.email && !m.userId && (
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => sendInvite.mutate(m.id)}
                              disabled={sendInvite.isPending}
                              data-testid={`button-invite-${m.id}`}
                            >
                              <Send className="h-3.5 w-3.5 mr-1" />
                              {m.portalInviteStatus === "pending" ? "Resend" : "Invite"}
                            </Button>
                          )}
                          {m.userId && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Lock className="h-3 w-3" />
                              Account linked
                            </div>
                          )}
                          {!m.isPrimaryContact && (
                            <Button
                              size="icon"
                              variant="ghost"
                              className="h-8 w-8 text-destructive"
                              onClick={() => {
                                if (confirm(`Remove ${m.name} from portal access?`)) {
                                  removeMember.mutate(m.id);
                                }
                              }}
                              data-testid={`button-remove-member-${m.id}`}
                            >
                              <Trash2 className="h-3.5 w-3.5" />
                            </Button>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
            {selectedTalentId && (
              <Card>
                <CardContent className="pt-6">
                  <CollaboratorRolesManager surface="talent" talentId={selectedTalentId} inPanel={true} />
                </CardContent>
              </Card>
            )}
          </TabsContent>
        )}
      </Tabs>

      {/* ── Contact upsert dialog ───────────────────────────────── */}
      <Dialog
        open={contactDialog.open}
        onOpenChange={(open) =>
          setContactDialog((s) => ({ ...s, open, editing: open ? s.editing : null }))
        }
      >
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>{contactDialog.editing ? "Edit Contact" : "Add Contact"}</DialogTitle>
            <DialogDescription>
              People you can quickly drop onto a call sheet.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            <div className="space-y-1">
              <Label htmlFor="contact-name">Name *</Label>
              <Input
                id="contact-name"
                value={contactForm.name}
                onChange={(e) => setContactForm({ ...contactForm, name: e.target.value })}
                data-testid="input-contact-name"
              />
            </div>
            <div className="space-y-1">
              <Label>Role</Label>
              <Select
                value={contactForm.role}
                onValueChange={(v) =>
                  setContactForm({ ...contactForm, role: v as ContactRole })
                }
              >
                <SelectTrigger data-testid="select-contact-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {ROLE_OPTIONS.map((r) => (
                    <SelectItem key={r.value} value={r.value}>
                      {r.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1">
                <Label htmlFor="contact-email">Email</Label>
                <Input
                  id="contact-email"
                  type="email"
                  value={contactForm.email}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, email: e.target.value })
                  }
                  data-testid="input-contact-email"
                />
              </div>
              <div className="space-y-1">
                <Label htmlFor="contact-phone">Phone</Label>
                <Input
                  id="contact-phone"
                  value={contactForm.phone}
                  onChange={(e) =>
                    setContactForm({ ...contactForm, phone: e.target.value })
                  }
                  data-testid="input-contact-phone"
                />
              </div>
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-company">Company</Label>
              <Input
                id="contact-company"
                value={contactForm.company}
                onChange={(e) =>
                  setContactForm({ ...contactForm, company: e.target.value })
                }
                data-testid="input-contact-company"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-address">Address</Label>
              <Input
                id="contact-address"
                value={contactForm.address}
                onChange={(e) =>
                  setContactForm({ ...contactForm, address: e.target.value })
                }
                data-testid="input-contact-address"
              />
            </div>
            <div className="space-y-1">
              <Label htmlFor="contact-notes">Notes</Label>
              <Textarea
                id="contact-notes"
                rows={2}
                value={contactForm.notes}
                onChange={(e) =>
                  setContactForm({ ...contactForm, notes: e.target.value })
                }
                data-testid="input-contact-notes"
              />
            </div>
            <div className="flex items-center justify-between">
              <Label htmlFor="contact-fav" className="text-sm">
                Favorite (always-pin)
              </Label>
              <Switch
                id="contact-fav"
                checked={contactForm.isFavorite}
                onCheckedChange={(checked) =>
                  setContactForm({ ...contactForm, isFavorite: checked })
                }
                data-testid="switch-contact-favorite"
              />
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="ghost"
              onClick={() => setContactDialog({ open: false, editing: null })}
            >
              Cancel
            </Button>
            <Button
              onClick={() => upsertContact.mutate()}
              disabled={!contactForm.name || upsertContact.isPending}
              data-testid="button-save-contact"
            >
              {upsertContact.isPending && <Loader2 className="h-4 w-4 mr-1 animate-spin" />}
              Save
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Members explainer (Team tab) — one-time per user ───────── */}
      <Dialog
        open={membersExplainerOpen}
        onOpenChange={(open) => {
          setMembersExplainerOpen(open);
          // Closing for any reason (X, backdrop, "Got it") counts as seen.
          if (!open && !membersExplainerSeen) ackExplainer.mutate("members");
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="dialog-members-explainer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserCog className="h-5 w-5 text-primary" />
              These are your members
            </DialogTitle>
            <DialogDescription>
              The Team tab is for your <strong>static band/troupe members</strong> — the people
              who stay with the act run after run.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>A member gets <strong>broad access</strong>:</p>
            <ul className="list-disc pl-5 space-y-1 text-muted-foreground">
              <li>Every one of the act's events</li>
              <li>Every call sheet</li>
              <li>Calendar access to every event, plus automatic calendar pushes</li>
            </ul>
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-3 text-amber-900 dark:text-amber-100">
              <p className="font-medium">Don't add one-off collaborators here.</p>
              <p className="mt-1">
                A lighting director, sound tech, or driver who's only on a single event or run is a{" "}
                <strong>collaborator</strong> — add them under the <strong>Contacts</strong> tab
                instead, so they only see the call sheets they're listed on.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setMembersExplainerOpen(false)}
              data-testid="button-members-explainer-ack"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Collaborators explainer (Contacts tab) — one-time per user ── */}
      <Dialog
        open={collabExplainerOpen}
        onOpenChange={(open) => {
          setCollabExplainerOpen(open);
          if (!open && !collabExplainerSeen) ackExplainer.mutate("collaborators");
        }}
      >
        <DialogContent className="sm:max-w-lg" data-testid="dialog-collaborators-explainer">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <ContactIcon className="h-5 w-5 text-primary" />
              These are your collaborators
            </DialogTitle>
            <DialogDescription>
              The Contacts tab (Address Book) is for <strong>rotating or one-off people</strong> —
              a lighting director, sound tech, driver, or other crew who may only be on a
              particular event or run.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 text-sm">
            <p>
              A collaborator only gets access to the <strong>call sheets they're specifically
              listed on</strong> — never the full event calendar or every call sheet.
            </p>
            <div className="rounded-md border border-amber-300 bg-amber-50 dark:border-amber-900 dark:bg-amber-950/40 p-3 text-amber-900 dark:text-amber-100">
              <p className="font-medium">These are not members.</p>
              <p className="mt-1">
                Static band/troupe members who stay with the act belong in the{" "}
                <strong>Team</strong> tab — adding them here as a contact would under-share and
                leave them without their normal access.
              </p>
            </div>
          </div>
          <DialogFooter>
            <Button
              onClick={() => setCollabExplainerOpen(false)}
              data-testid="button-collaborators-explainer-ack"
            >
              Got it
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
